import { uploadData } from "aws-amplify/storage";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import { v4 as uuidv4 } from "uuid";
import * as actions from "./actions";
import { REFRESH_TENANCY_CUSTOMIZATION_REQUEST } from "./actions";

import errorHandler from "common/errorHandler";

const getTenant = (state) => state.userLogin.tenant;

// Uploads a logo under the tenant's own folder (public/tenants/<tenant>/customization/), the only
// place a signed-in user may write, and returns its CDN URL.
export function* uploadLogo(file) {
    const tenant = yield select(getTenant);
    const fileName = `tenants/${tenant.tenantId}/customization/${uuidv4()}${file.name.slice(file.name.lastIndexOf("."))}`;
    const uploadTask = uploadData({
        path: `public/${fileName}`,
        data: file,
        options: {
            contentType: file.type,
            bucket: {
                bucketName: "pretectum-media",
                region: process.env.REACT_APP_BUCKET_REGION,
            },
        },
    });
    const result = yield call(() => uploadTask.result);
    return `${process.env.REACT_APP_MEDIA_CDN_URL}${result.path}`; // The CloudFront URL
}

function* getTenancyCustomization(api) {
    let response = yield call([api, api.get], `${Endpoints.tenants}/api/v1/brand`);
    return response.data;
}

function* updateTenancyCustomization(api, customizationData) {
    let response = yield call([api, api.put], `${Endpoints.tenants}/api/v1/brand`, customizationData);
    return response.data;
}

function* postTenancyCustomization(api, customizationData) {
    let response = yield call([api, api.post], `${Endpoints.tenants}/api/v1/brand`, customizationData);
    return response.data;
}

function* deleteTenancyCustomization(api) {
    let response = yield call([api, api.delete], `${Endpoints.tenants}/api/v1/brand`);
    return response.data;
}

export function* fetchTenancyCustomizationRequest(api) {
    try {
        let customizationData = yield call(getTenancyCustomization, api);
        yield put(actions.getTenancyCustomizationSuccess(customizationData));
    } catch (error) {
        if (error.response && error.response.status === 404) {
            yield put(actions.getTenancyCustomizationSuccess(null));
        } else {
            let errorObject = errorHandler(error, "Get", "tenancy customization");
            yield put(actions.getTenancyCustomizationFailure(errorObject));
        }
    }
}

export function* createTenancyCustomizationRequest(api, { customizationData }) {
    try {
        // Strip server-managed fields the API rejects on write
        // eslint-disable-next-line no-unused-vars
        const { tenantId, createdBy, updatedBy, createdDate, updatedDate, ...payload } = customizationData;
        customizationData = payload;
        if (customizationData.logo instanceof File) {
            customizationData.logo = yield call(uploadLogo, customizationData.logo);
        } else if (!customizationData.logo) {
            delete customizationData.logo;
        }
        let newCustomization = yield call(postTenancyCustomization, api, customizationData);
        yield put(actions.createTenancyCustomizationSuccess(newCustomization));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "tenancy customization");
        yield put(actions.createTenancyCustomizationFailure(errorObject));
    }
}

export function* updateTenancyCustomizationRequest(api, { customizationData }) {
    try {
        // Strip server-managed fields the API rejects on write
        // eslint-disable-next-line no-unused-vars
        const { tenantId, createdBy, updatedBy, createdDate, updatedDate, ...payload } = customizationData;
        customizationData = payload;
        // Handle the file upload if there is an image file
        if (customizationData.logo instanceof File) {
            customizationData.logo = yield call(uploadLogo, customizationData.logo);
        }
        const updatedCustomization = yield call(updateTenancyCustomization, api, customizationData);
        yield put(actions.updateTenancyCustomizationSuccess(updatedCustomization));
    } catch (error) {
        const errorObject = errorHandler(error, "Update", "tenancy customization");
        yield put(actions.updateTenancyCustomizationFailure(errorObject));
    }
}

export function* deleteTenancyCustomizationRequest(api) {
    try {
        yield call(deleteTenancyCustomization, api);
        yield put(actions.deleteTenancyCustomizationSuccess());
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "tenancy customization");
        yield put(actions.deleteTenancyCustomizationFailure(errorObject));
    }
}

export function* watchFetchTenancyCustomizationRequest(api) {
    yield call(fetchTenancyCustomizationRequest, api);
}

export function* watchCreateTenancyCustomizationRequest(api, { payload }) {
    yield call(createTenancyCustomizationRequest, api, payload);
}

export function* watchUpdateTenancyCustomizationRequest(api, { payload }) {
    yield call(updateTenancyCustomizationRequest, api, payload);
}

export function* watchDeleteTenancyCustomizationRequest(api, { payload }) {
    yield call(deleteTenancyCustomizationRequest, api, payload);
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_TENANCY_CUSTOMIZATION_REQUEST, watchFetchTenancyCustomizationRequest, api);
    yield takeLatest(REFRESH_TENANCY_CUSTOMIZATION_REQUEST, watchFetchTenancyCustomizationRequest, api);
    yield takeLatest(actions.CREATE_TENANCY_CUSTOMIZATION_REQUEST, watchCreateTenancyCustomizationRequest, api);
    yield takeLatest(actions.UPDATE_TENANCY_CUSTOMIZATION_REQUEST, watchUpdateTenancyCustomizationRequest, api);
    yield takeLatest(actions.DELETE_TENANCY_CUSTOMIZATION_REQUEST, watchDeleteTenancyCustomizationRequest, api);
}
